/**
 * Public SmartImprove intake API client — Feb 2026 redesign.
 *
 * Unauthenticated workshop draft surface. Mirrors the protected consultant
 * CRUD shape but uses the public `/api/smartimprove/intake/*` endpoints +
 * the resume_token minted at intake creation.
 *
 * Multi-draft local storage (Feb 2026 resume UX):
 *   - We persist an **array** of draft refs in localStorage so a single
 *     user/email can resume multiple in-progress engagements.
 *   - Each ref carries the bare minimum to resume — id + resume_token plus
 *     light metadata (company_name / email / updated_at / step) that lets
 *     the resume modal render without an extra round-trip.
 *   - Live data (sites, findings, status) is always reconciled against
 *     the backend GET so the modal never lies — submitted drafts are
 *     filtered out, and stale local refs are pruned automatically.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API });

const STORAGE_KEY = "smartimprove_public_intake_drafts_v2";
const LEGACY_KEY = "smartimprove_public_intake_draft_v1";

function _readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    }
    // One-time migration — promote the legacy single-draft key.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const obj = JSON.parse(legacy);
      if (obj && obj.id && obj.resume_token) {
        const arr = [{ ...obj, migrated_at: new Date().toISOString() }];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
        localStorage.removeItem(LEGACY_KEY);
        return arr;
      }
    }
  } catch {
    /* localStorage disabled or corrupt — fall through */
  }
  return [];
}

function _writeAll(arr) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

/** All locally-known draft refs (oldest first). */
export function listDraftRefs() {
  return _readAll();
}

/** The legacy single-draft accessor — returns the MOST-RECENTLY-TOUCHED draft
 *  so existing code paths that rehydrate on mount keep working without
 *  any caller changes. */
export function loadDraftRef() {
  const arr = _readAll();
  if (!arr.length) return null;
  return [...arr].sort(
    (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
  )[0];
}

/** Add or update a draft ref. Identified by `id` — keeps one entry per
 *  engagement; updates metadata (`updated_at`, `step`, counts, etc.). */
export function saveDraftRef(ref) {
  if (!ref || !ref.id || !ref.resume_token) return;
  const arr = _readAll();
  const idx = arr.findIndex((d) => d.id === ref.id);
  const next = {
    ...(idx >= 0 ? arr[idx] : {}),
    ...ref,
    updated_at: new Date().toISOString(),
  };
  if (idx >= 0) arr[idx] = next;
  else arr.push(next);
  _writeAll(arr);
}

/** Remove a single draft ref (used after Submit or 409 from backend). */
export function clearDraftRef(id) {
  if (!id) {
    _writeAll([]);
    return;
  }
  const arr = _readAll().filter((d) => d.id !== id);
  _writeAll(arr);
}

/** Create a new SmartImprove public draft (Step 1). Returns the engagement
 *  id and the resume_token used for every subsequent draft call. */
export const startIntakeDraft = (payload) =>
  ax.post("/intake", payload).then((r) => r.data);

/** Resume an existing draft — returns { engagement, sites, findings }. */
export const resumeIntakeDraft = (id, token) =>
  ax.get(`/intake/${id}`, { params: { token } }).then((r) => r.data);

export const patchEngagementDraft = (id, token, payload) =>
  ax.patch(`/intake/${id}`, payload, { params: { token } }).then((r) => r.data);

export const addSiteDraft = (id, token, payload) =>
  ax
    .post(`/intake/${id}/sites`, payload, { params: { token } })
    .then((r) => r.data);

export const updateSiteDraft = (id, siteId, token, payload) =>
  ax
    .patch(`/intake/${id}/sites/${siteId}`, payload, { params: { token } })
    .then((r) => r.data);

export const deleteSiteDraft = (id, siteId, token) =>
  ax
    .delete(`/intake/${id}/sites/${siteId}`, { params: { token } })
    .then((r) => r.data);

export const addFindingDraft = (id, token, payload) =>
  ax
    .post(`/intake/${id}/findings`, payload, { params: { token } })
    .then((r) => r.data);

export const updateFindingDraft = (id, findingId, token, payload) =>
  ax
    .patch(`/intake/${id}/findings/${findingId}`, payload, { params: { token } })
    .then((r) => r.data);

export const deleteFindingDraft = (id, findingId, token) =>
  ax
    .delete(`/intake/${id}/findings/${findingId}`, { params: { token } })
    .then((r) => r.data);

export const submitIntakeDraft = (id, token) =>
  ax.post(`/intake/${id}/submit`, null, { params: { token } }).then((r) => r.data);

// --- Public read-only catalogue endpoints (Feb 2026) ------------------------
// These do not require a resume_token because the underlying admin Issue /
// KPI libraries are consultant-curated reference catalogues.
export const getIntakeTaxonomy = () =>
  ax.get(`/intake/taxonomy`).then((r) => r.data);

export const getIssueTemplateSuggestions = (process, subProcess) =>
  ax
    .get(`/intake/templates/issues`, {
      params: { process, ...(subProcess ? { sub_process: subProcess } : {}) },
    })
    .then((r) => r.data);

export const getKpiTemplateSuggestions = (process, subProcess) =>
  ax
    .get(`/intake/templates/kpis`, {
      params: { process, ...(subProcess ? { sub_process: subProcess } : {}) },
    })
    .then((r) => r.data);
